import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import shortid from "shortid";

import { assertNativeError, delayForIndexing } from "@examspark/common";
import {
  toAWSDate,
  validateAcademicCalendar,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  useAppContext,
  useMutationCreateAcademicCalendar,
  useMutationDeleteAcademicCalendar,
  useMutationSetDefaultWorkspaceCalendar,
  useMutationUpdateAcademicCalendar,
  useQueryGetAcademicCalendar,
  useQueryGetDefaultWorkspaceCalendar,
} from "@examspark/common-app";
import { resourceVisibilityMeta } from "@examspark/common-meta";
import {
  CautionBanner,
  ConfirmModal,
  FormContext,
  InfoBanner,
  toast,
} from "@examspark/common-ui";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import { AcademicCalendar, GradingPeriod, Weekday } from "@examspark/types";

import Loading from "../../common/Loading";

import {
  CalendarForm,
  GradingPeriodWithId,
  HolidayWithId,
} from "./WorkspaceCalendarForm";

export const EditWorkspaceCalendarRoute = () => {
  const id = useParams<{ id: string }>().id;

  const { data, isLoading } = useQueryGetAcademicCalendar({
    input: {
      id: id ?? "",
      sk: "academic-calendar#v0",
    },
  });

  if (isLoading) {
    return <Loading>Loading Calendar...</Loading>;
  }

  if (!data) {
    return <div>Calendar not found</div>;
  }

  return <EditWorkspaceCalendar cal={data} />;
};

const EditWorkspaceCalendar = ({ cal }: { cal: AcademicCalendar }) => {
  const navigate = useNavigate();
  const name = useState<string>(cal.name ?? "Untitled Calendar");
  const startAt = useState<string | null>(cal.startAt);
  const endAt = useState<string | null>(cal.endAt);
  const instructionWeekdays = useState<Weekday[]>(
    cal.instructionWeekdays ?? [],
  );
  const visibility = useState(cal.visibility ?? null);

  const holidays = useArrayReducer<HolidayWithId>(
    cal.nonInstructionDates?.map((holiday) => ({
      ...holiday,
      id: shortid(),
    })) ?? [],
  );

  const gradingPeriodLength = useState<string | null>(
    cal.gradingPeriodLength ?? null,
  );

  // ensure there are at least 6 grading periods
  const gradingPeriods = useArrayReducer<GradingPeriodWithId>(
    Array(6)
      .fill(null)
      .map((_, idx) => ({
        label: `Grading Period ${idx + 1}`,
        startAt: toAWSDate(new Date()),
        endAt: toAWSDate(new Date()),

        ...cal.gradingPeriods?.[idx],
        id: shortid(),
      })),
  );

  const [context] = useAppContext();
  const workspaceId = context.workspaceUser?.workspace?.id;

  const mutationSave = useMutationCreateAcademicCalendar();
  const mutationUpdate = useMutationUpdateAcademicCalendar();
  const defaultCalendarQuery = useQueryGetDefaultWorkspaceCalendar(
    context.workspaceUser?.workspace?.id,
  );
  const deleteCalendarMutation = useMutationDeleteAcademicCalendar();
  const setDefaultWorkspaceCalendarMutation =
    useMutationSetDefaultWorkspaceCalendar();

  const client = useQueryClient();

  const handleDelete = async () => {
    await ConfirmModal.show({
      onResolving: async (result) => {
        if (!result) return;

        const defaultCalendar = await defaultCalendarQuery.refetch();
        if (defaultCalendar.data?.id === cal.id) {
          alert(
            "Default calendars cannot be deleted. Set another calendar as default first.",
          );
          return;
        }

        await deleteCalendarMutation.mutateAsync({
          id: cal.id,
        });
        await delayForIndexing();
        toast.success("Calendar Deleted");
        await Promise.all([
          client.invalidateQueries(["academic-calendar"]),
          client.invalidateQueries(["academic-calendars"]),
        ]);
        navigate("/calendar/workspace");
      },
      children: (
        <div style={{ padding: "1rem" }}>
          <CautionBanner variant="plain">
            Are you sure you want to delete this calendar? This action cannot be
            undone.
          </CautionBanner>
        </div>
      ),
    });
  };

  const handleArchive = async () => {
    await ConfirmModal.show({
      onResolving: async (result) => {
        if (!result) return;
        mutationUpdate.mutateAsync({
          id: cal.id,
          workspaceId: cal.workspaceId,
          isArchived: true,
        });
        await delayForIndexing();
        toast.success("Calendar Archived");
        await Promise.all([
          client.invalidateQueries(["academic-calendar"]),
          client.invalidateQueries(["academic-calendars"]),
        ]);
        navigate("/calendar/workspace");
      },
      children: (
        <div style={{ padding: "1rem" }}>
          <CautionBanner variant="plain">
            You are about to archive the calendar {cal.name} in your workspace.
            It will still be visible but can no longer be used.
            <br />
            <br />
            Are you sure you want to archive this calendar?
          </CautionBanner>
        </div>
      ),
    });
  };
  const handleDuplicate = async () => {
    const confirm = await ConfirmModal.show({
      children: (
        <div style={{ padding: "1rem" }}>
          <InfoBanner variant="plain">
            You are about to create an editable copy of {cal.name} in your
            workspace.
            <br />
            <br />
            Are you sure you want to duplicate this calendar?
          </InfoBanner>
        </div>
      ),
    });
    if (!confirm) return;
    if (!workspaceId) {
      console.error("no workspace id");
      return;
    }
    const newCalendar: Omit<AcademicCalendar, "id" | "sk"> = {
      ...cal,
      workspaceId,
      name: `${cal.name} (Copy)`,
      visibility: resourceVisibilityMeta.WORKSPACE.value,
    };

    const savedCal = await mutationSave.mutateAsync(newCalendar);

    navigate(`/calendar/workspace/${savedCal.data?.createAcademicCalendar.id}`);
  };

  const onSave = async () => {
    if (!workspaceId) {
      console.error("no workspace id");
      return;
    }

    if (workspaceId !== cal.workspaceId) {
      toast.error("You cannot edit a calendar from another workspace");
      return;
    }

    try {
      const sortedNonInstructionDates = holidays.values
        .map((holiday) => ({
          startAt: holiday.startAt,
          endAt: holiday.endAt,
          label: holiday.label,
          type: holiday.type,
        }))
        .sort((a, b) => {
          // our AWSDates are strings, but since they are yyyy-mm-dd, they still
          // sort correctly
          return (
            a.startAt.localeCompare(b.startAt) ||
            (a.label ?? "").localeCompare(b.label ?? "")
          );
        });

      const calendar: Omit<AcademicCalendar, "sk"> = {
        id: cal.id,
        schemaVersion: "2020-01-01",
        workspaceId,
        name: name[0],
        startAt: startAt[0] as string,
        endAt: endAt[0] as string,
        visibility: visibility[0],
        instructionWeekdays: instructionWeekdays[0],
        nonInstructionDates: sortedNonInstructionDates,
        gradingPeriodLength: gradingPeriodLength[0],
        gradingPeriods: gradingPeriods.values
          .map((gradingPeriod) => ({
            startAt: gradingPeriod.startAt,
            endAt: gradingPeriod.endAt,
            label: gradingPeriod.label,
          }))
          .filter(
            (_, idx) => idx < (gradingPeriodLength[0] === "6_WEEKS" ? 6 : 4),
          ) as GradingPeriod[], // assure TS that start/end times are set,
      };

      validateAcademicCalendar(calendar);
      await mutationUpdate.mutateAsync(calendar);
    } catch (err) {
      assertNativeError(err);
      toast.error(`Error saving calendar. ${err.message}`);
      return;
    }

    await Promise.all([
      client.invalidateQueries(["academic-calendar"]),
      client.invalidateQueries(["academic-calendars"]),
    ]);

    toast.success("Calendar saved");
  };

  const onSetAsDefault = async () => {
    if (!cal) return;

    const workspaceId = context.workspace?.id;
    if (!workspaceId) return;

    const confirmed = await ConfirmModal.show({
      children: (
        <div style={{ padding: "1rem" }}>
          <CautionBanner variant="plain">
            This will set "{cal.name}" as the default for your entire workspace
            ({context.workspace?.name}). Are you sure?
          </CautionBanner>
        </div>
      ),
    });
    if (!confirmed) return;

    await onSave();
    await setDefaultWorkspaceCalendarMutation.mutateAsync({
      id: workspaceId,
      sk: "workspace",
      defaultAcademicCalendarId: cal.id,
    });
    toast.success("Default calendar set");
  };

  const isOwnCalendar = cal.workspaceId === workspaceId;
  const isArchived = cal.isArchived ?? false;

  return (
    <FormContext.Provider value={{ disabled: !isOwnCalendar }}>
      <CalendarForm
        action="Edit"
        name={name}
        startAt={startAt}
        endAt={endAt}
        isDefault={defaultCalendarQuery.data?.id === cal.id}
        instructionWeekdays={instructionWeekdays}
        holidays={holidays}
        visibility={visibility}
        gradingPeriodLength={gradingPeriodLength}
        gradingPeriods={gradingPeriods}
        onSetAsDefault={isArchived ? undefined : onSetAsDefault}
        onArchive={isOwnCalendar ? handleArchive : undefined}
        onDelete={isOwnCalendar ? handleDelete : undefined}
        onDuplicate={handleDuplicate}
        onSave={onSave}
      />
    </FormContext.Provider>
  );
};
